import { Container, Stack } from '@instabox/consumer-web-ui'
import cn from 'classnames'
import { FC, ReactNode, useEffect, useState } from 'react'

import Banner, { BannerPosition } from '../components/Banner'
import { GetBannersQuery } from '../graphql/__generated__/types'
import { Unarray } from '../types'
import styles from './bannerContainer.module.scss'

const DISMISS_BANNER_TOKEN = 'dismissed_banners'

interface BannerContainerProps {
  children: ReactNode | ReactNode[]
  banners: GetBannersQuery['banners']
  position?: BannerPosition
  layout?: 'compact'
}

export const BannerContainer: FC<BannerContainerProps> = ({
  children,
  banners,
  position = 'relative',
  layout,
}) => {
  const [dismissedBanners, setDismissedBanners] = useState<string[]>([])
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
    try {
      const data = JSON.parse(localStorage.getItem(DISMISS_BANNER_TOKEN))
      setDismissedBanners(Array.isArray(data) ? data : [])
    } catch (_err) {
      // invalid LS content, fail silently
      setDismissedBanners([])
    }
  }, [])

  const closeBanner = (banner: Unarray<GetBannersQuery['banners']>) => {
    if (!banner.dismissible) {
      return null
    }
    const newDismissedBanners = [...dismissedBanners, banner.id]
    setDismissedBanners(newDismissedBanners)
    localStorage.setItem(DISMISS_BANNER_TOKEN, JSON.stringify(newDismissedBanners))
  }

  if (!isMounted) return null

  const displayBanners = banners?.map((banner, index) => {
    if (dismissedBanners.includes(banner.id)) return null
    return (
      <Banner
        key={banner.id}
        {...banner}
        onClose={() => closeBanner(banner)}
        stagger={index}
        position={position}
      />
    )
  })

  return (
    <>
      {displayBanners?.length > 0 && (
        <>
          {layout === 'compact' ? (
            <Stack className={styles.compactContainer}>{displayBanners}</Stack>
          ) : (
            <Container
              verticalPadding="md"
              className={cn(
                styles.container,
                position?.startsWith('fixed-') && styles.fixed,
                position === 'fixed-top' && styles.top,
                position === 'fixed-bottom' && styles.bottom,
              )}>
              <Stack>{displayBanners}</Stack>
            </Container>
          )}
        </>
      )}
      {children}
    </>
  )
}

export default BannerContainer
